<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Retrospective</span>
      <h1>Run a Steam Retrospective Whenever the Mood Strikes</h1>
      <p>
        Turn your Steam backlog into a story. With steamplayti.me you can assemble a detailed Steam retrospective
        that highlights wins, time sinks, and co-op memories across any date range.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Launch your retrospective</router-link>
        <a class="seo-secondary-link" href="#retrospective-playbook">View playbook →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">🧾</span>
          <div>Chronicle how your games evolved each month with friendly copy you can share in communities.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🧠</span>
          <div>Spot learning moments — which genres fuel focus, which ones drain it, and when you really need a break.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">💬</span>
          <div>Export insights to newsletters, clan updates, or personal journals with zero manual logging.</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="retrospective-playbook">
      <h2>The Steam Retrospective Playbook</h2>
      <p>
        A good retrospective is actionable. We surface the key segments that help you appreciate your past months
        and set the tone for what comes next.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Highlights Reel</h3>
          <p>Top five games, hours invested, and why each session mattered in a single glance.</p>
        </div>
        <div class="seo-card">
          <h3>Momentum Tracker</h3>
          <p>Compare rolling windows to understand if you are ramping up, cooling down, or ready to retire a title.</p>
        </div>
        <div class="seo-card">
          <h3>Next Actions</h3>
          <p>Identify backlog priorities with playtime context so you can plan the next sprint intentionally.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Make Reflection a Ritual</h2>
      <p>
        Forget hunting through the Steam client or spreadsheets. steamplayti.me keeps your data organized and repeatable
        so you can run retrospectives monthly, quarterly, or annually.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Date range control</div>
          <div class="value">Flexible</div>
        </div>
        <div class="seo-stat">
          <div class="label">Export options</div>
          <div class="value">Copy & share</div>
        </div>
        <div class="seo-stat">
          <div class="label">Community ready</div>
          <div class="value">Yes</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Steam Retrospective FAQs</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>What data do I need?</h3>
          <p>Just provide a public Steam profile. We fetch playtime and game names — no private data or passwords.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I run retrospectives for friends?</h3>
          <p>Yes. Any public Steam ID can be tracked. Share the resulting URL so everyone can add commentary.</p>
        </div>
        <div class="seo-faq-item">
          <h3>How long is history retained?</h3>
          <p>We continue collecting playtime while the profile stays public, giving you a living archive for future retrospectives.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-retrospective" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const META_DESCRIPTION = 'Turn your Steam history into a retrospective with shareable highlights, momentum tracking, and backlog priorities. Keep reflection easy with steamplayti.me.';

const META = {
  title: 'Steam Retrospective Builder & Backlog Insights | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'Steam retrospective, Steam recap tool, Steam play history, Steam backlog retrospective, Steam gaming journal',
  canonical: 'https://steamplayti.me/page/steam-retrospective/',
  ogTitle: 'Steam Retrospective Builder & Backlog Insights',
  ogDescription: 'Create a Steam retrospective on demand with automated data capture and storytelling prompts from steamplayti.me.',
  ogUrl: 'https://steamplayti.me/page/steam-retrospective/',
  twitterTitle: 'Steam Retrospective Builder & Backlog Insights',
  twitterDescription: 'Capture Steam highlights, backlog priorities, and momentum with an on-demand retrospective powered by steamplayti.me.',
  structuredData: pageStructuredData({
    name: 'Steam Retrospective',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-retrospective/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Retrospective', path: 'https://steamplayti.me/page/steam-retrospective/' },
    ],
  }),
};

export default {
  name: 'SteamRetrospectivePage',
  components: { SeoRelatedTools },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
